import { useMemo } from 'react'
import type { Tweet } from '../types'
import { formatRelativeTime, cn } from '../lib/utils'
import { useAlertStore } from '../stores/alertStore'

interface TweetCardProps {
  tweet: Tweet
  showAccount?: boolean
}

function TweetCard({ tweet, showAccount = true }: TweetCardProps) {
  const { keywords } = useAlertStore()

  const highlightedContent = useMemo(() => {
    let content = tweet.content

    // Escape HTML
    content = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')

    // Highlight matched keywords
    for (const matchedKw of tweet.matched_keywords) {
      const keyword = keywords.find((k) => k.keyword === matchedKw)
      const color = keyword?.color || '#fef08a'

      try {
        const escaped = matchedKw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const regex = new RegExp(`(${escaped})`, 'gi')
        content = content.replace(
          regex,
          `<mark style="background-color: ${color}; padding: 0 2px; border-radius: 2px;">$1</mark>`
        )
      } catch {
        // Invalid regex, skip
      }
    }

    // Make URLs clickable
    content = content.replace(
      /(https?:\/\/[^\s]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary-600 dark:text-primary-400 hover:underline">$1</a>'
    )

    // Make mentions clickable
    content = content.replace(
      /@(\w+)/g,
      '<a href="https://twitter.com/$1" target="_blank" rel="noopener noreferrer" class="text-primary-600 dark:text-primary-400 hover:underline">@$1</a>'
    )

    // Make hashtags clickable
    content = content.replace(
      /#(\w+)/g,
      '<a href="https://twitter.com/search?q=%23$1" target="_blank" rel="noopener noreferrer" class="text-primary-600 dark:text-primary-400 hover:underline">#$1</a>'
    )

    return content
  }, [tweet.content, tweet.matched_keywords, keywords])

  const hasAlert = tweet.matched_keywords.length > 0

  return (
    <div
      className={cn(
        'card p-4 transition-all hover:shadow-md',
        hasAlert && 'border-l-4 border-l-red-500 alert-pulse'
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          {showAccount && tweet.account && (
            <>
              <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold">
                {tweet.account.display_name?.[0]?.toUpperCase() || tweet.author_username[0].toUpperCase()}
              </div>
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {tweet.account.display_name || tweet.author_username}
                </div>
                <a
                  href={`https://twitter.com/${tweet.author_username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
                >
                  @{tweet.author_username}
                </a>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {formatRelativeTime(tweet.created_at)}
          </span>
          {tweet.tweet_url && (
            <a
              href={tweet.tweet_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              title="View on X"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
          )}
        </div>
      </div>

      {/* Content */}
      <div
        className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words"
        dangerouslySetInnerHTML={{ __html: highlightedContent }}
      />

      {/* Media */}
      {tweet.media_urls.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {tweet.media_urls.slice(0, 4).map((url, index) => (
            <a
              key={index}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="block aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden"
            >
              <img
                src={url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </a>
          ))}
        </div>
      )}

      {/* Keywords */}
      {hasAlert && (
        <div className="mt-3 flex flex-wrap gap-2">
          {tweet.matched_keywords.map((kw) => {
            const keyword = keywords.find((k) => k.keyword === kw)
            return (
              <span
                key={kw}
                className="px-2 py-0.5 text-xs font-medium rounded-full"
                style={{ backgroundColor: keyword?.color || '#fef08a' }}
              >
                {kw}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default TweetCard
